// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Extension, HealthcareService, Practitioner, Schedule, Slot } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPE_LEGACY_CODE_MAP } from '@polarissleep/scheduling';
import {
  formatFhirDateTimeInTimezone,
  getTimezoneExtensionValue,
  isValidTimezone,
  SCHEDULING_V2_PROVENANCE_TAG,
} from '@polarissleep/scheduling-next';
import {
  AVAILABILITY_BLOCK_CREATED_BY_EXTENSION_URL,
  FHIR_TIMEZONE_EXTENSION_URL,
  POLARIS_ENV_TAG_SYSTEM,
  SCHEDULING_PARAMETERS_EXTENSION_URL,
  SERVICE_TYPE_REFERENCE_EXTENSION_URL,
  V2_OVERRIDE_TAG_CODE,
  V2_OVERRIDE_TAG_SYSTEM,
} from './constants';
import { type AvailabilityOverrideDraft, V2_DAY_CODES, type V2DayCode, type WeeklyAvailabilityWindow } from './types';

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MINUTES_PER_DAY = 24 * 60;

export interface ScheduleAvailabilityWindow {
  start: Date;
  end: Date;
}

export interface AvailabilityValidationOptions {
  healthcareServices: HealthcareService[];
  serviceId?: string;
  timezone?: string;
  schedule?: Schedule;
  existingOverrideSlots?: Slot[];
}

export interface OverrideSlotChanges {
  create: Slot[];
  update: Slot[];
  delete: Slot[];
}

export function getPractitionerTimezone(practitioner: Practitioner): string {
  const timezone = getTimezoneExtensionValue(practitioner.extension);
  if (!timezone || !isValidTimezone(timezone)) {
    throw new Error('Provider timezone must be a valid IANA timezone.');
  }
  return timezone;
}

export function withPractitionerTimezone(practitioner: Practitioner, timezone: string): Practitioner {
  if (!isValidTimezone(timezone)) {
    throw new Error('Provider timezone must be a valid IANA timezone.');
  }
  return {
    ...practitioner,
    extension: [
      ...(practitioner.extension?.filter((extension) => extension.url !== FHIR_TIMEZONE_EXTENSION_URL) ?? []),
      { url: FHIR_TIMEZONE_EXTENSION_URL, valueCode: timezone },
    ],
  };
}

export function getSchedulingParameters(
  resource: HealthcareService | Schedule,
  serviceId?: string
): Extension | undefined {
  return resource.extension?.find((extension) => {
    if (extension.url !== SCHEDULING_PARAMETERS_EXTENSION_URL) {
      return false;
    }
    if (resource.resourceType === 'HealthcareService' || !serviceId) {
      return true;
    }
    return extension.extension?.some(
      (child) => child.url === 'service' && child.valueReference?.reference === `HealthcareService/${serviceId}`
    );
  });
}

export function getSchedulingParameterMinutes(
  resource: HealthcareService | Schedule,
  parameter: 'duration' | 'bufferAfter' | 'alignmentInterval' | 'alignmentOffset',
  serviceId?: string
): number {
  const extension = getSchedulingParameters(resource, serviceId);
  const duration = extension?.extension?.find((child) => child.url === parameter)?.valueDuration;
  return duration?.unit === 'h' ? (duration.value ?? 0) * 60 : (duration?.value ?? 0);
}

export function getServiceReference(service: HealthcareService): string {
  if (!service.id) {
    throw new Error('HealthcareService must have an ID.');
  }
  return `HealthcareService/${service.id}`;
}

export function getServiceCode(service: HealthcareService): { system?: string; code: string; display?: string } {
  const coding = service.type?.flatMap((type) => type.coding ?? []).find((item) => item.code);
  if (!coding?.code) {
    throw new Error(`HealthcareService/${service.id ?? 'unknown'} must have a service type code.`);
  }
  return { system: coding.system, code: coding.code, display: coding.display ?? service.name };
}

export function readWeeklyAvailability(schedule: Schedule, serviceId: string): WeeklyAvailabilityWindow[] {
  const availability = getSchedulingParameters(schedule, serviceId)?.extension?.find(
    (extension) => extension.url === 'availability'
  );
  return (availability?.extension ?? [])
    .filter((extension) => extension.url === 'availableTime')
    .map((availableTime) => {
      const dayOfWeek = availableTime.extension?.find((extension) => extension.url === 'daysOfWeek')?.valueCode;
      const startTime = availableTime.extension?.find((extension) => extension.url === 'availableStartTime')?.valueTime;
      const endTime = availableTime.extension?.find((extension) => extension.url === 'availableEndTime')?.valueTime;
      if (!isDayCode(dayOfWeek) || !startTime || !endTime) {
        return undefined;
      }
      return { dayOfWeek, startTime: startTime.slice(0, 5), endTime: endTime.slice(0, 5) };
    })
    .filter((window): window is WeeklyAvailabilityWindow => Boolean(window));
}

export function withAllWeekdays(windows: WeeklyAvailabilityWindow[]): WeeklyAvailabilityWindow[] {
  return V2_DAY_CODES.flatMap((day) => {
    const configuredWindows = windows.filter((window) => window.dayOfWeek === day);
    return configuredWindows.length > 0 ? configuredWindows : [{ dayOfWeek: day, startTime: '', endTime: '' }];
  });
}

/**
 * Expands service-specific Schedule weekly windows into display windows for a calendar range.
 * This is presentation-only: recurring availability remains virtual and no Slot is created.
 */
export function expandScheduleAvailability(
  schedule: Schedule,
  serviceId: string,
  timezone: string,
  range: { start: Date; end: Date }
): ScheduleAvailabilityWindow[] {
  const weeklyWindows = readWeeklyAvailability(schedule, serviceId);
  if (weeklyWindows.length === 0) {
    return [];
  }

  const firstProviderDate = formatFhirDateTimeInTimezone(range.start, timezone).slice(0, 10);
  const lastProviderDate = formatFhirDateTimeInTimezone(range.end, timezone).slice(0, 10);
  const firstDate = parseCalendarDate(firstProviderDate);
  const lastDate = parseCalendarDate(lastProviderDate);
  const windows: ScheduleAvailabilityWindow[] = [];

  for (let date = firstDate; date <= lastDate; date = addCalendarDays(date, 1)) {
    const dateString = formatCalendarDate(date);
    const dayOfWeek = V2_DAY_CODES[date.getUTCDay()];
    for (const weeklyWindow of weeklyWindows) {
      if (weeklyWindow.dayOfWeek !== dayOfWeek) {
        continue;
      }
      const start = new Date(localDateTimeToFhir(dateString, weeklyWindow.startTime, timezone));
      const end = new Date(localDateTimeToFhir(dateString, weeklyWindow.endTime, timezone));
      if (start < range.end && end > range.start) {
        windows.push({ start, end });
      }
    }
  }

  return windows;
}

export function buildScheduleV2Availability(
  schedule: Schedule,
  services: HealthcareService[],
  draft: Pick<
    import('./types').V2AvailabilityDraft,
    'timezone' | 'bufferAfterMinutes' | 'healthcareServiceId' | 'healthcareServiceIds' | 'weeklyAvailability'
  >
): Schedule {
  if (!isValidTimezone(draft.timezone)) {
    throw new Error('Provider timezone must be a valid IANA timezone.');
  }
  const selectedServiceIds = draft.healthcareServiceIds?.length
    ? draft.healthcareServiceIds
    : [draft.healthcareServiceId];
  const selectedServices = selectedServiceIds.flatMap((id) => {
    const service = services.find((candidate) => candidate.id === id);
    return service ? [service] : [];
  });
  if (selectedServices.length !== selectedServiceIds.length) {
    throw new Error(`Availability service ${draft.healthcareServiceId} is unavailable.`);
  }
  const configuredWeeklyAvailability = draft.weeklyAvailability.filter(
    (window) => window.startTime !== '' || window.endTime !== ''
  );
  for (const service of selectedServices) {
    validateAvailabilityDraft(configuredWeeklyAvailability, [], {
      healthcareServices: services,
      serviceId: service.id,
      schedule,
    });
  }
  const existingParameters = (schedule.extension ?? []).filter((extension) => {
    if (extension.url !== SCHEDULING_PARAMETERS_EXTENSION_URL) {
      return true;
    }
    const service = extension.extension?.find((child) => child.url === 'service')?.valueReference?.reference;
    // V2 scheduling parameters must be linked to a known HealthcareService.
    // Drop unscoped/legacy parameters instead of broadening availability.
    return Boolean(service) && !services.some((candidate) => service === getServiceReference(candidate));
  });
  const parameters = selectedServices.map((service) =>
    buildScheduleParameters(service, {
      ...draft,
      weeklyAvailability: configuredWeeklyAvailability,
      bufferAfterMinutes: draft.bufferAfterMinutes ?? getSchedulingParameterMinutes(service, 'bufferAfter'),
    })
  );
  const managedServiceCodes = new Set(services.map((service) => getServiceCode(service).code));
  const existingServiceTypes = (schedule.serviceType ?? []).filter((serviceType) => {
    const reference = serviceType.extension?.find((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
      ?.valueReference?.reference;
    const code = serviceType.coding?.find((coding) => coding.code)?.code;
    const isManagedReference = services.some((candidate) => reference === getServiceReference(candidate));
    return !isManagedReference && !(code && managedServiceCodes.has(code));
  });
  const serviceTypes = selectedServices.map((service) => {
    const coding = getServiceCode(service);
    return {
      text: coding.display,
      coding: [{ system: coding.system, code: coding.code, display: coding.display }],
      extension: [
        {
          url: SERVICE_TYPE_REFERENCE_EXTENSION_URL,
          valueReference: { reference: getServiceReference(service), display: coding.display },
        },
      ],
    };
  });

  return {
    ...schedule,
    active: true,
    serviceType: [...existingServiceTypes, ...serviceTypes],
    extension: [...existingParameters, ...parameters],
  };
}

function buildScheduleParameters(
  service: HealthcareService,
  draft: Pick<import('./types').V2AvailabilityDraft, 'timezone' | 'bufferAfterMinutes' | 'weeklyAvailability'>
): Extension {
  const durationMinutes = getSchedulingParameterMinutes(service, 'duration');
  const bufferAfterMinutes = draft.bufferAfterMinutes ?? getSchedulingParameterMinutes(service, 'bufferAfter');
  const alignmentIntervalMinutes = durationMinutes + bufferAfterMinutes;
  const parameterExtensions: Extension[] = [
    { url: 'duration', valueDuration: { value: durationMinutes, unit: 'min' } },
    { url: 'bufferBefore', valueDuration: { value: 0, unit: 'min' } },
    { url: 'bufferAfter', valueDuration: { value: bufferAfterMinutes, unit: 'min' } },
    { url: 'alignmentInterval', valueDuration: { value: alignmentIntervalMinutes, unit: 'min' } },
    { url: 'alignmentOffset', valueDuration: { value: 0, unit: 'min' } },
    { url: 'service', valueReference: { reference: getServiceReference(service), display: service.name } },
    { url: 'timezone', valueCode: draft.timezone },
    { url: 'alignmentTimezone', valueCode: draft.timezone },
  ];
  // Keep explicit empty availability. Omitting this extension is interpreted
  // by the scheduling service as unrestricted availability.
  parameterExtensions.push(
    draft.weeklyAvailability.length > 0
      ? {
          url: 'availability',
          extension: draft.weeklyAvailability.map((window) => ({
            url: 'availableTime',
            extension: [
              { url: 'daysOfWeek', valueCode: window.dayOfWeek },
              { url: 'availableStartTime', valueTime: `${window.startTime}:00` },
              { url: 'availableEndTime', valueTime: `${window.endTime}:00` },
            ],
          })),
        }
      : { url: 'availability', valueBoolean: false }
  );
  return {
    url: SCHEDULING_PARAMETERS_EXTENSION_URL,
    extension: parameterExtensions,
  };
}

export function validateAvailabilityDraft(
  weeklyAvailability: WeeklyAvailabilityWindow[],
  overrides: AvailabilityOverrideDraft[],
  options: AvailabilityValidationOptions
): void {
  const services = new Map(options.healthcareServices.map((service) => [service.id, service]));
  if (services.size === 0) {
    throw new Error('At least one active HealthcareService is required.');
  }
  const weeklyServices = options.serviceId
    ? [services.get(options.serviceId)].filter((service): service is HealthcareService => Boolean(service))
    : [...services.values()];
  if (weeklyServices.length === 0) {
    throw new Error(`Availability service ${options.serviceId} is unavailable.`);
  }
  validateWindows(
    weeklyAvailability.filter((window) => window.startTime !== '' || window.endTime !== ''),
    'Weekly hours'
  );

  const seenOverrideIds = new Set<string>();
  for (const override of overrides) {
    if (!override.id && !services.has(override.healthcareServiceId)) {
      throw new Error('Override must target a compatible HealthcareService.');
    }
    if (override.id && seenOverrideIds.has(override.id)) {
      throw new Error(`Override ${override.id} appears more than once.`);
    }
    if (override.id) {
      seenOverrideIds.add(override.id);
    }
    if (!services.has(override.healthcareServiceId)) {
      throw new Error(`Override service ${override.healthcareServiceId} is not compatible with this Schedule.`);
    }
    if (!isValidCalendarDate(override.date)) {
      throw new Error(`Invalid override date: ${override.date}`);
    }
    if (options.timezone) {
      localDateTimeToFhir(override.date, override.startTime, options.timezone);
      localDateTimeToFhir(override.date, override.endTime, options.timezone);
    }
    const service = services.get(override.healthcareServiceId);
    if (!service) {
      throw new Error('Override service is unavailable.');
    }
    validateWindows([
      { dayOfWeek: dayCodeForDate(override.date), startTime: override.startTime, endTime: override.endTime },
    ]);
  }

  const overridesByServiceDate = new Map<
    string,
    { serviceId: string; date: string; windows: WeeklyAvailabilityWindow[] }
  >();
  for (const override of overrides) {
    const key = `${override.healthcareServiceId}|${override.date}`;
    const group = overridesByServiceDate.get(key) ?? {
      serviceId: override.healthcareServiceId,
      date: override.date,
      windows: [],
    };
    group.windows.push({
      dayOfWeek: dayCodeForDate(override.date),
      startTime: override.startTime,
      endTime: override.endTime,
    });
    overridesByServiceDate.set(key, group);
  }
  for (const { serviceId, date, windows } of overridesByServiceDate.values()) {
    const service = services.get(serviceId);
    if (service) {
      const serviceLabel = getServiceCode(service).display || service.name || serviceId;
      validateWindows(windows, `Time off for ${serviceLabel} on ${formatReadableDate(date)}`);
    }
  }

  const existingBusyIds = new Set(
    (options.existingOverrideSlots ?? []).filter((slot) => slot.status === 'busy').map((slot) => slot.id)
  );
  for (const override of overrides) {
    if (override.id && existingBusyIds.has(override.id)) {
      throw new Error(`Booked Slot/${override.id} cannot be changed by availability authoring.`);
    }
  }
}

function validateWindows(windows: WeeklyAvailabilityWindow[], context = 'Availability'): void {
  const byDay = new Map<V2DayCode, WeeklyAvailabilityWindow[]>();
  for (const window of windows) {
    if (!isDayCode(window.dayOfWeek) || !TIME_PATTERN.test(window.startTime) || !TIME_PATTERN.test(window.endTime)) {
      throw new Error('Availability windows must use valid day codes and HH:mm times.');
    }
    const start = toMinutes(window.startTime);
    const end = toMinutes(window.endTime);
    if (start >= end || end > MINUTES_PER_DAY) {
      throw new Error(`Availability window ${window.startTime}-${window.endTime} is invalid.`);
    }
    const dayWindows = byDay.get(window.dayOfWeek) ?? [];
    dayWindows.push(window);
    byDay.set(window.dayOfWeek, dayWindows);
  }
  for (const [day, dayWindows] of byDay) {
    const sorted = [...dayWindows].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
    for (let index = 1; index < sorted.length; index += 1) {
      if (toMinutes(sorted[index].startTime) < toMinutes(sorted[index - 1].endTime)) {
        throw new Error(
          `${context}${context === 'Weekly hours' ? ` on ${formatDayCode(day)}` : ''} has overlapping windows: ${formatTimeForError(sorted[index - 1].startTime)}–${formatTimeForError(sorted[index - 1].endTime)} overlaps ${formatTimeForError(sorted[index].startTime)}–${formatTimeForError(sorted[index].endTime)}. Adjust or remove one of these windows.`
        );
      }
    }
  }
}

function formatDayCode(day: V2DayCode): string {
  const labels: Record<V2DayCode, string> = {
    sun: 'Sunday',
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
  };
  return labels[day];
}

function formatTimeForError(value: string): string {
  const [hours, minutes] = value.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

function formatReadableDate(date: string | undefined): string {
  if (date) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${date}T00:00:00Z`));
  }
  return 'selected date';
}

export function buildOverrideSlot(
  schedule: Schedule,
  service: HealthcareService,
  override: AvailabilityOverrideDraft,
  timezone: string,
  organizationId: string | undefined,
  environment: string | undefined,
  existing?: Slot,
  createdBy?: AvailabilityOverrideDraft['createdBy']
): Slot {
  if (!schedule.id) {
    throw new Error('Schedule must have an ID before creating an override.');
  }
  const start = localDateTimeToFhir(override.date, override.startTime, timezone);
  const end = localDateTimeToFhir(override.date, override.endTime, timezone);
  const coding = getServiceCode(service);
  let creatorExtension = existing?.extension;
  if (!existing && createdBy) {
    creatorExtension = [{ url: AVAILABILITY_BLOCK_CREATED_BY_EXTENSION_URL, valueReference: createdBy }];
  }
  const tags = [
    ...(existing?.meta?.tag ?? []).filter(
      (tag) => tag.system !== V2_OVERRIDE_TAG_SYSTEM && tag.system !== POLARIS_ENV_TAG_SYSTEM
    ),
    { system: V2_OVERRIDE_TAG_SYSTEM, code: V2_OVERRIDE_TAG_CODE },
    SCHEDULING_V2_PROVENANCE_TAG,
    ...(environment ? [{ system: POLARIS_ENV_TAG_SYSTEM, code: environment }] : []),
  ];
  return {
    ...existing,
    resourceType: 'Slot',
    id: existing?.id,
    schedule: { reference: `Schedule/${schedule.id}` },
    status: override.status,
    start,
    end,
    comment:
      override.reason ||
      (override.status === 'free' ? 'Medplum v2 availability addition' : 'Medplum v2 availability block'),
    serviceType: [{ coding: [{ system: coding.system, code: coding.code, display: coding.display }] }],
    extension: creatorExtension,
    meta: {
      ...existing?.meta,
      account: organizationId ? { reference: `Organization/${organizationId}` } : undefined,
      tag: tags,
    },
  };
}

export function getOverrideSlotDraft(
  slot: Slot,
  timezone: string,
  services: HealthcareService[]
): AvailabilityOverrideDraft | undefined {
  if (slot.status !== 'free' && slot.status !== 'busy-unavailable') {
    return undefined;
  }
  const serviceCoding = slot.serviceType?.[0]?.coding?.[0];
  const date = slot.start ? formatDateInTimezone(slot.start, timezone) : undefined;
  const startTime = slot.start ? formatTimeInTimezone(slot.start, timezone) : undefined;
  const endTime = slot.end ? formatTimeInTimezone(slot.end, timezone) : undefined;
  const service = services.find((candidate) => {
    const coding = getServiceCode(candidate);
    const slotCode = serviceCoding?.code;
    const canonicalSlotCode = slotCode ? APPOINTMENT_TYPE_LEGACY_CODE_MAP[slotCode] : undefined;
    return (
      coding.system === serviceCoding?.system &&
      (coding.code === slotCode || (canonicalSlotCode !== undefined && coding.code === canonicalSlotCode))
    );
  });
  if (!slot.id || !date || !startTime || !endTime || !service?.id) {
    return undefined;
  }
  return {
    id: slot.id,
    healthcareServiceId: service.id,
    date,
    startTime,
    endTime,
    reason: slot.comment && !slot.comment.startsWith('Medplum v2 availability ') ? slot.comment : undefined,
    status: slot.status === 'free' ? 'free' : 'busy-unavailable',
    createdBy: slot.extension?.find((extension) => extension.url === AVAILABILITY_BLOCK_CREATED_BY_EXTENSION_URL)
      ?.valueReference,
  };
}

export function calculateOverrideSlotChanges(existing: Slot[], desired: Slot[]): OverrideSlotChanges {
  const desiredById = new Map(desired.filter((slot) => slot.id).map((slot) => [slot.id, slot]));
  const create = desired.filter((slot) => !slot.id);
  const update: Slot[] = [];
  const remove: Slot[] = [];
  for (const current of existing) {
    if (!current.id) {
      continue;
    }
    const next = desiredById.get(current.id);
    if (['active', 'booked', 'busy', 'tentative', 'fulfilled', 'busy-tentative'].includes(current.status ?? '')) {
      throw new Error(`Override Slot/${current.id} cannot be changed while status is ${current.status}.`);
    }
    if (next) {
      update.push(next);
    } else {
      remove.push(current);
    }
  }
  return { create, update, delete: remove };
}

function isDayCode(value: string | undefined): value is V2DayCode {
  return Boolean(value && V2_DAY_CODES.includes(value as V2DayCode));
}

function toMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

function dayCodeForDate(date: string): V2DayCode {
  return V2_DAY_CODES[new Date(`${date}T00:00:00Z`).getUTCDay()];
}

function isValidCalendarDate(value: string): boolean {
  if (!DATE_PATTERN.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCFullYear() === year && candidate.getUTCMonth() === month - 1 && candidate.getUTCDate() === day;
}

export function localDateTimeToFhir(date: string, time: string, timezone: string): string {
  if (!DATE_PATTERN.test(date) || !TIME_PATTERN.test(time)) {
    throw new Error('Override date and time must be valid.');
  }
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  let candidate = new Date(Date.UTC(year, month - 1, day, hour, minute));
  for (let index = 0; index < 3; index += 1) {
    const formatted = formatFhirDateTimeInTimezone(candidate, timezone);
    const offsetHours = Number(formatted.slice(-5, -3));
    const offsetMinutes = Number(formatted.slice(-2));
    const sign = formatted.slice(-6, -5) === '-' ? -1 : 1;
    candidate = new Date(
      Date.UTC(year, month - 1, day, hour, minute) - sign * (offsetHours * 60 + offsetMinutes) * 60_000
    );
  }
  const result = formatFhirDateTimeInTimezone(candidate, timezone);
  if (result.slice(0, 16) !== `${date}T${time}`) {
    throw new Error(`Override time ${date} ${time} does not exist in ${timezone}.`);
  }
  const matchingCandidates = new Set<number>();
  for (let offsetMinutes = -36 * 60; offsetMinutes <= 36 * 60; offsetMinutes += 60) {
    const sample = new Date(candidate.getTime() + offsetMinutes * 60_000);
    const sampleFormatted = formatFhirDateTimeInTimezone(sample, timezone);
    const sampleOffsetSign = sampleFormatted.slice(-6, -5) === '-' ? -1 : 1;
    const sampleOffsetMinutes =
      sampleOffsetSign * (Number(sampleFormatted.slice(-5, -3)) * 60 + Number(sampleFormatted.slice(-2)));
    const matchingCandidate = new Date(Date.UTC(year, month - 1, day, hour, minute) - sampleOffsetMinutes * 60_000);
    if (formatFhirDateTimeInTimezone(matchingCandidate, timezone).slice(0, 16) === `${date}T${time}`) {
      matchingCandidates.add(matchingCandidate.getTime());
    }
  }
  if (matchingCandidates.size > 1) {
    throw new Error(`Override time ${date} ${time} is ambiguous in ${timezone}.`);
  }
  return result;
}

function parseCalendarDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function addCalendarDays(value: Date, days: number): Date {
  const result = new Date(value);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function formatCalendarDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function formatDateInTimezone(value: string, timezone: string): string {
  return formatFhirDateTimeInTimezone(value, timezone).slice(0, 10);
}

function formatTimeInTimezone(value: string, timezone: string): string {
  return formatFhirDateTimeInTimezone(value, timezone).slice(11, 16);
}
